import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { documentsApi, DocumentWithOCR, DocumentClassification, OCRResult } from '../api/documents';
import {
  canDecryptE2eeSilently,
  decryptE2eeDocument,
  decryptE2eeFile,
  decryptE2eeFileSilently,
} from '../utils/e2eeClient';
import { subscribeSseJson } from '../utils/sse';
import {
  displayFileType,
  formatClassifierValue,
  formatOcrStatusShort,
  humanizeCode,
} from '../utils/documentLabels';
import type { ClassifierConfig } from '../api/admin';
import { HelpTipButton } from '../components/HelpTipButton';
import { memberDisplayName } from '../utils/memberDisplay';
import ConfirmDialog from '../components/ConfirmDialog';
import DocumentBar from '../components/document/DocumentBar';
import DocumentMetadataModal from '../components/DocumentMetadataModal';
import DocumentPreview from '../components/DocumentPreview';
import '../components/DocumentPreview.css';
import { useIsAdvanced } from '../utils/viewMode';
import { useViewedAsSharedMember } from '../utils/useSharedMemberAccess';
import { useClassifiersConfig } from '../contexts/ClassifiersConfigContext';
import './DocumentView.css';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatAiJobStatus(s: string | null | undefined): string {
  if (s == null || s === '') return '—';
  const m: Record<string, string> = {
    queued: 'În așteptare',
    generating: 'În curs',
    completed: 'Finalizat',
    error: 'Eroare',
  };
  return m[s] || s;
}

/**
 * Tracks which reprocess button the user just clicked. Used to (a) prevent
 * double-clicks across all four buttons while one action is in flight, and
 * (b) so that — for the brief moment between click and the first SSE tick —
 * we know not to enable any other action.
 *
 * The actual *spinner* on each icon is still driven by the per-stage running
 * flags (isProcessing / isSummaryGenerating / isClassifying); that way a
 * "Rerun all" naturally moves the spinner from OCR → Summary → Classify as
 * each stage actually begins on the backend.
 */
type ReprocessAction = 'rerun-all' | 'rerun-ocr' | 'regenerate-summary' | 'reclassify' | null;

function pillClassForPipeline(stage: string): string {
  if (!stage) return 'doc-view-pill doc-view-pill--muted';
  if (stage === 'completed') return 'doc-view-pill doc-view-pill--ok';
  if (stage.includes('error') || stage === 'failed') return 'doc-view-pill doc-view-pill--err';
  return 'doc-view-pill doc-view-pill--progress';
}

function pillClassForAiStatus(s: string | null | undefined): string {
  if (s == null || s === '') return 'doc-view-pill doc-view-pill--muted';
  if (s === 'completed') return 'doc-view-pill doc-view-pill--ok';
  if (s === 'error') return 'doc-view-pill doc-view-pill--err';
  if (s === 'generating') return 'doc-view-pill doc-view-pill--progress';
  return 'doc-view-pill doc-view-pill--muted';
}

/**
 * Roll-up "tone" for the overall document pipeline — mirrors the helper
 * baked into `MemberDocumentCard` so the collapsed-panel pill on this
 * page reads identically to the pill on the document list card.
 *   error  → red    (anything errored)
 *   busy   → amber  (OCR / Summary / Classify currently running)
 *   ready  → teal   (OCR finished — incl. completed / summary_completed)
 *   idle   → slate  (brand-new doc, no status yet)
 */
type DocPipelineTone = 'busy' | 'error' | 'ready' | 'idle';

/**
 * Header-style status pill matching `MemberDocumentCard.renderStatusPill()`.
 * Uses the shared `.doc-card-status` styles defined in `App.css` so the
 * collapsed details panel on `DocumentView` and the list-page card stay
 * visually in lockstep. Renders as a non-interactive `<span>` so clicking
 * inside `<summary>` still toggles the panel open/closed.
 */
function DocPipelinePill({
  tone,
  label,
  title,
}: {
  tone: DocPipelineTone;
  label: string;
  title?: string;
}) {
  return (
    <span
      className={`doc-card-status doc-card-status--${tone} doc-view-details-status-pill`}
      title={title}
    >
      <span className="doc-card-status-dot" aria-hidden />
      {label}
    </span>
  );
}

/** Status pill; when status is error and detail exists, click opens full message in a popover. */
function AiJobPill({
  status,
  errorDetail,
  detailLabel,
}: {
  status: string | null | undefined;
  errorDetail: string | null | undefined;
  detailLabel: string;
}) {
  const pillClass = pillClassForAiStatus(status);
  const label = formatAiJobStatus(status);
  const err = errorDetail?.trim();
  if (status === 'error' && err) {
    return (
      <HelpTipButton
        content={err}
        ariaLabel={detailLabel}
        wide
        buttonClassName={`${pillClass} doc-view-pill--as-btn`}
      >
        <span className="doc-view-pill-dot" aria-hidden />
        {label}
      </HelpTipButton>
    );
  }
  return (
    <span className={pillClass}>
      <span className="doc-view-pill-dot" aria-hidden />
      {label}
    </span>
  );
}

type TabId = 'original' | 'markdown' | 'summary' | 'classification';

/**
 * Single-row presentation of one physical file inside a grouped
 * MedicalDocument. Renders a file-type icon, filename, size, and a
 * "Descarcă" button that streams the file via the per-file download
 * endpoint. The download button manages its own in-flight state so
 * the user can fire several downloads without blocking the whole row.
 */
const DocumentFileRow: React.FC<{
  documentId: number;
  fileId: number;
  filename: string;
  mime: string | null | undefined;
  size: number;
}> = ({ documentId, fileId, filename, mime, size }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const onDownload = async () => {
    setBusy(true);
    setErr('');
    try {
      await documentsApi.downloadFile(documentId, fileId, filename);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Eroare la descărcare');
    } finally {
      setBusy(false);
    }
  };

  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const isImage = ['png', 'jpg', 'jpeg', 'tiff', 'tif', 'heic', 'heif', 'gif', 'webp'].includes(
    ext
  );

  return (
    <li className="doc-view-files-row">
      <div className="doc-view-files-icon" aria-hidden>
        {isImage ? (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="9" cy="9" r="2" />
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
          </svg>
        ) : (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        )}
      </div>
      <div className="doc-view-files-meta">
        <div className="doc-view-files-name" title={filename}>
          {filename}
        </div>
        <div className="doc-view-files-sub">
          <span>{formatFileSize(size)}</span>
          {mime ? <span>· {mime}</span> : null}
          {err ? <span className="doc-view-files-error">· {err}</span> : null}
        </div>
      </div>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={onDownload}
        disabled={busy}
      >
        {busy ? (
          <>
            <span className="status-spinner" style={{ width: 12, height: 12 }} />
            Se descarcă…
          </>
        ) : (
          <>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Descarcă
          </>
        )}
      </button>
    </li>
  );
};

// Per-classifier display hints for the detail-panel chip rows.
//
// Value formatting (`code` → display label) is fully data-driven now —
// every classifier ships its own taxonomy entries through
// `useClassifiersConfig()` and `formatClassifierValue` does the lookup.
// What still lives here is purely cosmetic: badge color per classifier
// key and the optional `(facet)` / `(canonic)` disambiguator shown in
// advanced view. Unknown classifier keys fall through to a neutral
// slate badge.
function facetFormatterFor(
  classifiers: ClassifierConfig[],
  key: string
): (v: string) => string {
  return (v: string) => formatClassifierValue(classifiers, key, v);
}

function facetBadgeVariantFor(key: string): string {
  if (key === 'document_type') return 'badge-teal';
  if (key === 'specialty') return 'badge-blue';
  if (key === 'imaging_modality') return 'badge-amber';
  if (key === 'imaging_body_region') return 'badge-slate';
  return 'badge-slate';
}

function facetAdvancedLabelFor(key: string, fallback: string): string {
  // Advanced view appends a disambiguator that hints at the data
  // source ("facet" / "canonic") to help admins debug the classifier
  // output vs. user-set fields. Simplified view shows the plain label.
  if (key === 'document_type') return 'Tip document (clasificare)';
  if (key === 'specialty') return 'Specialitate (canonic)';
  if (key === 'imaging_modality') return 'Imagistică (facet)';
  if (key === 'imaging_body_region') return 'Regiuni anatomice (facet)';
  return fallback;
}

const DocumentView: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const isAdvanced = useIsAdvanced();
  const { classifiers } = useClassifiersConfig();
  const [document, setDocument] = useState<DocumentWithOCR | null>(null);
  // Resolve "do I have access to this member's whole family?" so the
  // breadcrumb can route family links to `/shared-members` instead of
  // the inaccessible family page. `document` is `null` on first render;
  // the hook tolerates `null` and returns `null` (treated as `false`).
  const viewedAsSharedMember =
    useViewedAsSharedMember(document?.document?.family_member_id ?? null) ?? false;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [activeAction, setActiveAction] = useState<ReprocessAction>(null);
  const [activeTab, setActiveTab] = useState<TabId>('original');

  // Refs for the rendered tab buttons so keyboard nav (Arrow / Home / End)
  // can move focus to the newly-selected tab — same pattern as MemberTabs.
  const tabsRef = useRef<Record<TabId, HTMLButtonElement | null>>({
    original: null,
    markdown: null,
    summary: null,
    classification: null,
  });

  // If the user flips to simplified mode while sitting on the now-hidden
  // Markdown tab, snap them back to Original.
  useEffect(() => {
    if (!isAdvanced && activeTab === 'markdown') {
      setActiveTab('original');
    }
  }, [isAdvanced, activeTab]);

  const [statusMessage, setStatusMessage] = useState('');
  const [currentStatus, setCurrentStatus] = useState('');
  const [summaryStatus, setSummaryStatus] = useState('');
  const [classificationStatus, setClassificationStatus] = useState('');
  const [showSource, setShowSource] = useState(false);
  const [showEditDocMeta, setShowEditDocMeta] = useState(false);
  const [decrypting, setDecrypting] = useState(false);
  const [decryptedUrl, setDecryptedUrl] = useState<string | null>(null);
  const [decryptedBlob, setDecryptedBlob] = useState<Blob | null>(null);
  const [decryptedMime, setDecryptedMime] = useState<string | null>(null);
  const [decryptedName, setDecryptedName] = useState<string | null>(null);
  const [canAutoDecrypt, setCanAutoDecrypt] = useState(false);
  const [autoDecryptStatus, setAutoDecryptStatus] = useState<'idle' | 'trying' | 'failed'>('idle');
  // For grouped documents the Document tab pages through every file.
  // Cache decrypted blobs by `file_id` so flipping back to a previously
  // viewed page is instant (no second decrypt round-trip). The cache
  // lives in a ref because it never needs to trigger a re-render — the
  // displayed page is driven by `previewFileIdx` + the flat
  // `decrypted*` state below.
  const [previewFileIdx, setPreviewFileIdx] = useState(0);
  const decryptedByFileId = useRef<
    Map<number, { url: string; blob: Blob; mime: string; name: string }>
  >(new Map());
  // Mirror of the currently-displayed page index, refreshed every
  // render so async decrypt callbacks can check "am I still the page
  // the user is looking at?" without taking a re-render dep on the
  // state itself. The auto-decrypt effect cancels via its own
  // `cancelled` flag, but the inner `decryptByIdx` doesn't see that
  // flag — without this guard, a slow decrypt of page A could land
  // its `setDecryptedUrl(urlA)` after the user already paged to B,
  // briefly painting A's bytes through B's viewer.
  const previewFileIdxRef = useRef(0);
  useEffect(() => {
    previewFileIdxRef.current = previewFileIdx;
  }, [previewFileIdx]);

  const docMeta = document?.document;
  const errorRef = useRef<HTMLDivElement | null>(null);

  // Resolve the file at a given page index. Returns null if the
  // document hasn't loaded yet or the index is out of range.
  const fileAtIdx = useCallback(
    (idx: number) => {
      const files = docMeta?.files ?? [];
      if (!files.length) return null;
      const sorted = [...files].sort((a, b) => a.file_index - b.file_index);
      return sorted[idx] || null;
    },
    [docMeta]
  );

  /** Decrypt the file at `idx` and swap it into the visible preview
   *  state. Uses the per-file cache so flipping back is instant. When
   *  `silent` is true and no remembered key is available, the helper
   *  resolves without doing anything (the UI shows the unlock CTA).
   *
   *  Returns `true` when decryption succeeded (or was a cache hit),
   *  `false` when no remembered key was available in silent mode.
   *  Throws on any other failure so the caller can surface the error. */
  const decryptByIdx = useCallback(
    async (idx: number, opts?: { silent?: boolean }): Promise<boolean> => {
      const file = fileAtIdx(idx);
      if (!file) return false;
      // Only push the decrypted entry into the visible preview state
      // when this `idx` is still the page the user is looking at.
      // Without this guard, a stale `decryptByIdx(0)` resolving after
      // the user paged to `idx=1` would overwrite the new page's URL
      // / blob with the old page's data and feed the wrong bytes into
      // the viewer.
      const swapIfCurrent = (entry: {
        url: string;
        blob: Blob;
        mime: string;
        name: string;
      }) => {
        if (previewFileIdxRef.current !== idx) return;
        setDecryptedUrl(entry.url);
        setDecryptedBlob(entry.blob);
        setDecryptedMime(entry.mime);
        setDecryptedName(entry.name);
      };
      // Cache hit — swap flat state instantly.
      const cached = decryptedByFileId.current.get(file.id);
      if (cached) {
        swapIfCurrent(cached);
        return true;
      }
      const id = parseInt(documentId || '0', 10);
      if (!id) return false;
      const fallback = {
        filename: file.original_filename || `pagina-${idx + 1}`,
        mime: file.mime_type || 'application/octet-stream',
      };
      try {
        const res = opts?.silent
          ? await decryptE2eeFileSilently(id, file.id, fallback)
          : await decryptE2eeFile(id, file.id, fallback);
        const blob = new Blob([res.bytes.buffer as ArrayBuffer], { type: res.mime });
        const url = URL.createObjectURL(blob);
        const entry = { url, blob, mime: res.mime, name: res.filename };
        // Always warm the cache (even when the user paged away mid-
        // decrypt) — coming back to this page is then a cache hit.
        decryptedByFileId.current.set(file.id, entry);
        swapIfCurrent(entry);
        return true;
      } catch (e: any) {
        if (opts?.silent && /E2EE key not available/i.test(String(e?.message))) {
          // Silent path with no remembered key — caller falls back to
          // the manual unlock UI; this isn't an error to surface.
          return false;
        }
        throw e;
      }
    },
    [documentId, fileAtIdx]
  );

  const loadDocument = useCallback(async () => {
    if (!documentId) return;
    try {
      setDocument(await documentsApi.getDocument(parseInt(documentId, 10)));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la incarcarea documentului');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    const ac = new AbortController();
    if (documentId) {
      setLoading(true);
      loadDocument();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      subscribeSseJson(`${apiUrl}/api/documents/${documentId}/status/stream`, {
        signal: ac.signal,
        onMessage: (data) => {
          try {
            setCurrentStatus(data.pipeline_status || data.status || '');
            setStatusMessage(data.message || '');
            if (data.status === 'completed' || data.status === 'summary_completed' || data.status === 'classification_completed') {
              setTimeout(loadDocument, 1000);
            }
            if (data.status === 'summary_generating') {
              setSummaryStatus('generating');
            } else if (data.status === 'summary_completed') {
              setSummaryStatus('completed');
            } else if (data.status === 'summary_error') {
              setSummaryStatus('error');
            }
            if (data.status === 'classification_generating') {
              setClassificationStatus('generating');
            } else if (data.status === 'classification_completed') {
              setClassificationStatus('completed');
            } else if (data.status === 'classification_error') {
              setClassificationStatus('error');
            }

            // Per-stage spinner clear — `/regenerate-summary` and
            // `/classify` now return 202 immediately and dispatch the
            // LLM call to Celery, so the spinner can't clear in the
            // handler's `finally` any more. We clear it once SSE reports
            // a terminal status for the matching stage. Rerun-OCR /
            // rerun-all flows still block on the HTTP response and
            // manage their own spinner lifecycle.
            const stage = data.status || data.pipeline_status;
            if (
              stage === 'summary_completed' ||
              stage === 'summary_error' ||
              data.summary_status === 'completed' ||
              data.summary_status === 'error'
            ) {
              setActiveAction((cur) =>
                cur === 'regenerate-summary' ? null : cur,
              );
              setRegenerating(false);
            }
            if (
              stage === 'classification_completed' ||
              stage === 'classification_error' ||
              data.classification_status === 'completed' ||
              data.classification_status === 'error'
            ) {
              setActiveAction((cur) => (cur === 'reclassify' ? null : cur));
              setClassifying(false);
            }
          } catch {}
        },
      }).catch(() => {});
    }
    return () => { ac.abort(); };
  }, [documentId, loadDocument]);

  // On unmount, revoke every cached Blob URL so we don't leak memory
  // for multi-file documents (one URL per page). The displayed URL is
  // always also in the cache, so revoking the cache covers it too.
  useEffect(() => {
    const cache = decryptedByFileId.current;
    return () => {
      cache.forEach((entry) => URL.revokeObjectURL(entry.url));
      cache.clear();
    };
  }, []);

  useEffect(() => {
    if (!error.trim()) return;
    window.setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }, [error]);

  // Determine whether we can decrypt without prompting.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ok = await canDecryptE2eeSilently();
        if (!cancelled) setCanAutoDecrypt(ok);
      } catch {
        if (!cancelled) setCanAutoDecrypt(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Clear the visible preview state when the user pages to a file
  // that isn't in the cache yet — otherwise we'd briefly render the
  // previous page until the new one finishes decrypting.
  useEffect(() => {
    const file = fileAtIdx(previewFileIdx);
    if (!file) return;
    if (decryptedByFileId.current.has(file.id)) return;
    setDecryptedUrl(null);
    setDecryptedBlob(null);
    setDecryptedMime(null);
    setDecryptedName(null);
  }, [previewFileIdx, fileAtIdx]);

  // Auto-decrypt the currently selected page silently whenever the
  // user has a remembered key + the cache is cold for that page. Runs
  // both on initial load (for `previewFileIdx === 0`) and on every
  // page flip; cache hits resolve instantly so a re-decrypt only fires
  // for never-viewed pages.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!documentId) return;
        if (!docMeta) return; // wait for the document metadata
        if (!canAutoDecrypt) return;
        const file = fileAtIdx(previewFileIdx);
        if (!file) return;
        if (decryptedByFileId.current.has(file.id)) {
          // Cache hit (e.g. flipping back to a previously viewed page).
          // Run the helper to swap state, but no network/decrypt work.
          await decryptByIdx(previewFileIdx, { silent: true });
          if (!cancelled) setAutoDecryptStatus('idle');
          return;
        }

        setAutoDecryptStatus('trying');
        setDecrypting(true);
        const ok = await decryptByIdx(previewFileIdx, { silent: true });
        if (cancelled) return;
        setAutoDecryptStatus(ok ? 'idle' : 'failed');
      } catch {
        // Silent decrypt best-effort; UI will show the manual unlock
        // CTA for this page.
        if (!cancelled) setAutoDecryptStatus('failed');
      } finally {
        if (!cancelled) setDecrypting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documentId, docMeta, canAutoDecrypt, previewFileIdx, decryptByIdx, fileAtIdx]);

  const unwrapFenced = (content: string) => {
    if (!content) return content;
    return content.replace(/```(?:markdown)?\s*\n?([\s\S]*?)\n?```/gim, (_, c) => c || _).trim();
  };

  // Optimistically patch the local OCR snapshot so the "Detalii document" pills
  // (and any other UI bound to ocr_result.*) reflect the new in-progress state
  // immediately, instead of waiting for the network round-trip + SSE push.
  const patchOcr = useCallback((patch: Partial<OCRResult>) => {
    setDocument((prev) => {
      if (!prev) return prev;
      const base: OCRResult = prev.ocr_result ?? ({
        id: 0,
        document_id: parseInt(documentId || '0', 10),
        version: 0,
        markdown_text: null,
        processing_date: new Date().toISOString(),
        confidence_score: null,
        processing_status: 'queued',
        pipeline_status: 'queued',
        summary: null,
        summary_status: null,
        summary_error: null,
        classification: null,
        classification_status: null,
        classification_error: null,
        page_count: null,
        table_count: null,
      } as OCRResult);
      return { ...prev, ocr_result: { ...base, ...patch } };
    });
  }, [documentId]);

  const handleRegenerateSummary = async () => {
    if (!documentId) return;
    setActiveAction('regenerate-summary');
    setRegenerating(true);
    setError('');
    setSummaryStatus('generating');
    // Flip the pipeline pill ("Stare" / "Etapa pipeline") to mirror what the
    // backend will report via SSE once the job is picked up — without this the
    // "Stare" field stays on "Finalizat" until the first SSE tick lands.
    setCurrentStatus('summary_generating');
    setStatusMessage('');
    patchOcr({
      summary_status: 'generating',
      summary_error: null,
      pipeline_status: 'summary_generating',
    });
    try {
      // 202-immediate; LLM call runs on Celery. The spinner is NOT
      // cleared in a finally — the SSE handler clears `activeAction` /
      // `regenerating` once a terminal `summary_status` arrives.
      await documentsApi.regenerateSummary(parseInt(documentId), 'ro');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la regenerarea rezumatului');
      patchOcr({ summary_status: 'error', pipeline_status: 'summary_error' });
      setCurrentStatus('summary_error');
      setSummaryStatus('error');
      // Dispatch itself failed — SSE will never report progress for
      // this attempt, so clear the spinner here.
      setRegenerating(false);
      setActiveAction(null);
    }
  };

  const handleClassify = async () => {
    if (!documentId) return;
    setActiveAction('reclassify');
    setClassifying(true);
    setError('');
    setClassificationStatus('generating');
    setCurrentStatus('classification_generating');
    setStatusMessage('');
    patchOcr({
      classification_status: 'generating',
      classification_error: null,
      pipeline_status: 'classification_generating',
    });
    try {
      // 202-immediate; the SSE handler clears `activeAction` /
      // `classifying` on terminal classification status.
      await documentsApi.classifyDocument(parseInt(documentId));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la clasificarea documentului');
      patchOcr({ classification_status: 'error', pipeline_status: 'classification_error' });
      setCurrentStatus('classification_error');
      setClassificationStatus('error');
      setClassifying(false);
      setActiveAction(null);
    }
  };

  // Delete-document confirm-dialog state. The bar's "Șterge document"
  // kebab item just flips `showDeleteConfirm`; the actual destructive
  // call runs out of `confirmDeleteDocument` so the dialog can show
  // an inline spinner via its `busy` prop and we can navigate away
  // with a one-shot flash banner on success.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState(false);

  const requestDelete = useCallback(() => {
    setError('');
    setShowDeleteConfirm(true);
  }, []);

  const confirmDeleteDocument = useCallback(async () => {
    if (!documentId || !document) return;
    setDeletingDocument(true);
    setError('');
    try {
      await documentsApi.deleteDocument(parseInt(documentId, 10));
      setShowDeleteConfirm(false);
      // One-shot flash via navigation state so the member dossier
      // can surface a confirmation banner after the redirect (same
      // pattern FamilyDetail uses when deleting a family).
      navigate(`/members/${document.document.family_member_id}`, {
        state: {
          flash: `Documentul „${document.document.original_filename}” a fost șters.`,
        },
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la ștergerea documentului');
      setShowDeleteConfirm(false);
    } finally {
      setDeletingDocument(false);
    }
  }, [documentId, document, navigate]);

  const [downloading, setDownloading] = useState(false);

  // Download flow that handles both server-stored (plain) and E2EE documents.
  // For E2EE: if we already have a decrypted blob in memory (because the user
  // viewed the Original tab), reuse it; otherwise trigger the unlock prompt.
  // For plain: hit the authenticated download endpoint and stream into a Blob.
  const handleDownload = useCallback(async () => {
    if (!documentId || !document) return;
    const id = parseInt(documentId, 10);
    if (!Number.isFinite(id)) return;
    setDownloading(true);
    setError('');
    try {
      const doc = document.document;
      const triggerSave = (href: string, filename: string) => {
        const a = window.document.createElement('a');
        a.href = href;
        a.download = filename;
        window.document.body.appendChild(a);
        a.click();
        a.remove();
      };

      const fileCount = doc.file_count ?? 1;
      const files = [...(doc.files || [])].sort((a, b) => a.file_index - b.file_index);

      if (fileCount > 1 && files.length > 1) {
        // Grouped document → build a ZIP client-side. The server can't
        // do this because every file is E2EE-encrypted with its own
        // DEK; only the client holds the keys to decrypt + repack.
        // Re-uses the per-file decrypt cache populated by the Document
        // tab so re-downloading after viewing is a single zip build.
        const { default: JSZip } = await import('jszip');
        const zip = new JSZip();
        const used = new Set<string>();
        for (let idx = 0; idx < files.length; idx++) {
          const f = files[idx];
          const cached = decryptedByFileId.current.get(f.id);
          let bytes: Uint8Array;
          let name: string;
          if (cached) {
            bytes = new Uint8Array(await cached.blob.arrayBuffer());
            name = cached.name || f.original_filename || `pagina-${idx + 1}`;
          } else {
            const res = await decryptE2eeFile(id, f.id, {
              filename: f.original_filename || `pagina-${idx + 1}`,
              mime: f.mime_type || 'application/octet-stream',
            });
            bytes = res.bytes;
            name = res.filename || f.original_filename || `pagina-${idx + 1}`;
            // Warm the cache so a subsequent re-download (or a flip
            // to that page in the preview) is instant.
            const blob = new Blob([bytes.buffer as ArrayBuffer], { type: res.mime });
            const url = URL.createObjectURL(blob);
            decryptedByFileId.current.set(f.id, {
              url,
              blob,
              mime: res.mime,
              name,
            });
          }
          // De-dup filenames so two files named the same don't
          // overwrite each other inside the zip.
          let entryName = name;
          if (used.has(entryName)) {
            const dot = entryName.lastIndexOf('.');
            const stem = dot > 0 ? entryName.slice(0, dot) : entryName;
            const ext = dot > 0 ? entryName.slice(dot) : '';
            entryName = `${stem} (${idx + 1})${ext}`;
          }
          used.add(entryName);
          zip.file(entryName, bytes.buffer as ArrayBuffer);
        }
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(zipBlob);
        try {
          const base = (doc.original_filename || `document-${id}`).replace(/\.[^.]+$/, '');
          triggerSave(url, `${base}.zip`);
        } finally {
          // Defer revoke to let the browser actually start the
          // download — revoking immediately can race the navigation
          // in some browsers.
          setTimeout(() => URL.revokeObjectURL(url), 30_000);
        }
      } else if (decryptedUrl && decryptedName) {
        // Single-file doc with an already-decrypted blob in memory →
        // reuse it (no extra round-trip).
        triggerSave(decryptedUrl, decryptedName);
      } else {
        // Single-file doc, cold cache → decrypt then save.
        const res = await decryptE2eeDocument(id);
        const blob = new Blob([res.bytes.buffer as ArrayBuffer], { type: res.mime });
        const url = URL.createObjectURL(blob);
        if (decryptedUrl) URL.revokeObjectURL(decryptedUrl);
        setDecryptedUrl(url);
        setDecryptedBlob(blob);
        setDecryptedMime(res.mime);
        setDecryptedName(res.filename);
        triggerSave(url, res.filename || doc.original_filename);
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'Eroare la descărcarea documentului. Dacă este criptat, asigură-te că l-ai deblocat în tab-ul Document.'
      );
    } finally {
      setDownloading(false);
    }
  }, [documentId, document, decryptedUrl, decryptedName]);

  const handleRerun = async () => {
    if (!documentId) return;
    setActiveAction('rerun-ocr');
    setError('');
    // Flip the pipeline pill and the SSE-backed currentStatus to "queued" right
    // away so the user sees the action register, even before the request is
    // accepted by the server. The OCR-only re-run also clears any prior OCR
    // error so the popover state matches the new attempt.
    setCurrentStatus('queued');
    setStatusMessage('');
    patchOcr({ pipeline_status: 'queued', processing_status: 'queued' });
    try {
      const id = parseInt(documentId);
      // Always-on E2EE: server can't OCR ciphertext, decrypt locally + POST plaintext.
      const dec = await decryptE2eeDocument(id);
      await documentsApi.rerunOcrWithOriginalFile(id, {
        bytes: dec.bytes,
        filename: dec.filename,
        mime: dec.mime,
        ocrOnly: true,
      });
      loadDocument();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la repornirea procesarii OCR');
      patchOcr({ pipeline_status: 'error', processing_status: 'error' });
      setCurrentStatus('error');
    } finally {
      setActiveAction(null);
    }
  };

  // Full pipeline re-run: OCR → summary → classification, all in one go.
  // For E2EE docs we have to decrypt locally and POST the plaintext bytes back
  // (the same protocol the upload flow uses); for server-stored docs the
  // backend already has the original file.
  const handleRerunAll = async () => {
    if (!documentId) return;
    setActiveAction('rerun-all');
    setError('');
    setCurrentStatus('queued');
    setStatusMessage('');
    // IMPORTANT: pre-flip summary/classification to 'queued' (NOT 'generating').
    // The actual stage flips to 'generating' will happen via SSE as each stage
    // truly begins on the backend. This keeps the spinner on a single icon at
    // a time — first OCR, then Summary, then Classify — matching the actual
    // backend execution order, instead of falsely spinning all three at once.
    patchOcr({
      pipeline_status: 'queued',
      processing_status: 'queued',
      summary_status: 'queued',
      summary_error: null,
      classification_status: 'queued',
      classification_error: null,
    });
    setSummaryStatus('queued');
    setClassificationStatus('queued');
    try {
      const id = parseInt(documentId);
      // Always-on E2EE: server can't OCR ciphertext, decrypt locally + POST plaintext.
      const dec = await decryptE2eeDocument(id);
      await documentsApi.rerunOcrWithOriginalFile(id, {
        bytes: dec.bytes,
        filename: dec.filename,
        mime: dec.mime,
        ocrOnly: false,
      });
      loadDocument();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la reprocesarea completă');
      patchOcr({
        pipeline_status: 'error',
        processing_status: 'error',
        summary_status: 'error',
        classification_status: 'error',
      });
      setCurrentStatus('error');
      // Clear pre-flipped 'queued' states on the SSE-mirrored local vars so
      // the summary/classification spinners don't stay spinning when the
      // request itself failed and no SSE events will ever arrive.
      setSummaryStatus('error');
      setClassificationStatus('error');
    } finally {
      setActiveAction(null);
    }
  };

  // Effective status string used by both isProcessing and isOcrRunning. We
  // resolve it once so the two flags stay in sync.
  const effectiveStatus = currentStatus || document?.ocr_result?.pipeline_status || document?.ocr_result?.processing_status || '';

  // Broad "is anything pipeline-related happening" — used for the top
  // processing banner and the `anyBusy` reprocess-button lockout. Includes
  // post-OCR AI stages on purpose.
  const isProcessing = [
    'queued',
    'processing_started',
    'loading_images',
    'ocr_extraction',
    'processing',
    'summary_generating',
    'classification_generating',
  ].includes(effectiveStatus);

  // Narrow "OCR stage is currently running" — used to drive the OCR icon
  // spinner specifically. Must NOT include summary/classification generating
  // states, otherwise clicking Reclasify would also light up the OCR icon
  // even though OCR isn't actually running.
  const isOcrRunning = [
    'queued',
    'processing_started',
    'loading_images',
    'ocr_extraction',
    'processing',
  ].includes(effectiveStatus) || document?.ocr_result?.processing_status === 'ocr_extraction';

  const isSummaryGenerating = summaryStatus === 'generating' || regenerating ||
    document?.ocr_result?.summary_status === 'generating';

  const isClassifying = classificationStatus === 'generating' || classifying ||
    document?.ocr_result?.classification_status === 'generating';

  const statusLabel: Record<string, string> = {
    queued: 'In coada de procesare',
    processing_started: 'Procesare initiata',
    loading_images: 'Se incarca imaginile',
    ocr_extraction: 'Extractie OCR',
    processing: 'Procesare avansata',
    completed: 'Finalizat',
    error: 'Eroare',
    summary_generating: 'Se genereaza rezumatul AI',
    summary_completed: 'Rezumat generat',
    summary_error: 'Eroare la generarea rezumatului',
    classification_generating: 'Se clasifica documentul',
    classification_completed: 'Document clasificat',
    classification_error: 'Eroare la clasificare',
  };

  // Same labels in both modes — the only difference is that simplified hides
  // the raw "Markdown" (OCR text dump) tab; non-tech users have no use for
  // the verbatim post-OCR text.
  const tabs: { id: TabId; label: string }[] = [
    { id: 'original', label: 'Document' },
    ...(isAdvanced ? [{ id: 'markdown' as const, label: 'Markdown' }] : []),
    { id: 'summary', label: 'Rezumat AI' },
    { id: 'classification', label: 'Clasificare AI' },
  ];

  // Roving tabindex / arrow-key nav — order is computed from the live
  // `tabs` array because Markdown is hidden in simplified mode. Mirrors the
  // MemberTabs / WAI-ARIA tablist pattern used elsewhere in the app.
  const onTabsKeyDown = (ev: React.KeyboardEvent<HTMLButtonElement>) => {
    const order = tabs.map((t) => t.id);
    const idx = order.indexOf(activeTab);
    if (idx < 0) return;
    let next: number | null = null;
    switch (ev.key) {
      case 'ArrowRight':
        next = (idx + 1) % order.length;
        break;
      case 'ArrowLeft':
        next = (idx - 1 + order.length) % order.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = order.length - 1;
        break;
      default:
        return;
    }
    ev.preventDefault();
    const nextId = order[next!];
    setActiveTab(nextId);
    window.requestAnimationFrame(() => {
      tabsRef.current[nextId]?.focus();
    });
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="skeleton skeleton-line" style={{ width: 280, height: 28 }} />
        <div className="skeleton skeleton-card" style={{ marginTop: 24, height: 120 }} />
        <div className="skeleton skeleton-card" style={{ marginTop: 16, height: 400 }} />
      </div>
    );
  }

  if (!document) {
    return <div className="page-container"><div className="alert alert-error">Document negasit</div></div>;
  }

  const doc = document.document;
  const ocr = document.ocr_result;
  const familyLabel = (document.family_name || '').trim() || 'Familie';
  const memberLabel = memberDisplayName({ name: document.member_name });
  const resolvedStatus = currentStatus || ocr?.pipeline_status || ocr?.processing_status || '';
  const pipelineStage = ocr?.pipeline_status || resolvedStatus;
  const pipelineLabel =
    formatOcrStatusShort(pipelineStage) ||
    statusLabel[pipelineStage] ||
    pipelineStage ||
    '—';
  // Per-stage actions (regenerate summary / reclassify) only need OCR text to
  // exist — not a clean rolled-up pipeline. Otherwise a downstream failure
  // (e.g. classifier errored) would lock the user out of retrying just the
  // failed stage, forcing a full re-OCR. We treat OCR as "done" if the
  // dedicated OCR status reports completed OR we already have markdown.
  const ocrDone = Boolean(
    ocr && (ocr.processing_status === 'completed' || (ocr.markdown_text && ocr.markdown_text.length > 0))
  );
  // Single "is anything reprocess-related happening" flag used to disable all
  // four reprocess buttons in lockstep. Includes both server-confirmed stage
  // states (isProcessing / isSummaryGenerating / isClassifying) and the
  // very-brief click-to-accept window covered by `activeAction`.
  const anyBusy = activeAction !== null || isProcessing || isSummaryGenerating || isClassifying;

  return (
    <div className="page-bar-page document-view-page">
      {/* Full-width sticky bar — sibling of `.page-container` so it
          spans the entire viewport like the navbar above it. The
          bar replaces the old `.doc-view-hero` block (title + sub +
          reprocess icon strip + delete button); the title now reads
          as the leaf breadcrumb and the file-meta line lives in the
          existing "Detalii document" panel below. */}
      <DocumentBar
        originalFilename={doc.original_filename}
        familyMemberId={doc.family_member_id}
        memberLabel={memberLabel}
        familyId={document.family_id}
        familyName={familyLabel}
        viewedAsSharedMember={viewedAsSharedMember}
        onDownload={handleDownload}
        downloading={downloading}
        onReocr={handleRerun}
        isOcrRunning={isOcrRunning}
        onRegenerateSummary={handleRegenerateSummary}
        isSummaryGenerating={isSummaryGenerating}
        onReclassify={handleClassify}
        isClassifying={isClassifying}
        anyBusy={anyBusy}
        ocrDone={ocrDone}
        onRerunAll={handleRerunAll}
        isRerunAllInFlight={activeAction === 'rerun-all'}
        onEdit={() => setShowEditDocMeta(true)}
        onDelete={requestDelete}
      />

      <div className="page-container page-bar-page-content">
      {error && (
        <div ref={errorRef} className="alert alert-error">
          {error}
        </div>
      )}

      {/* Processing status */}
      {isProcessing && (
        <div className="status-bar">
          <div className="status-spinner" />
          <div className="status-text">
            {statusLabel[resolvedStatus] || resolvedStatus}
            {statusMessage && <> — {statusMessage}</>}
          </div>
        </div>
      )}

      {(() => {
        // Compact meta line under the panel title. Mirrors the old hero
        // sub-line: small slate text with `·` separators (rendered via
        // CSS gaps so wrapping on mobile is clean and never leaves a
        // dangling separator). Empty pieces are filtered out.
        const fileCount = doc.file_count ?? 1;
        const isGrouped = fileCount > 1;
        const uploadDateLabel = (() => {
          try {
            return new Date(doc.upload_date).toLocaleDateString('ro-RO');
          } catch {
            return '';
          }
        })();
        const metaParts: string[] = [];
        if (isGrouped) {
          metaParts.push(`${fileCount} fișiere`);
        } else {
          const tip = displayFileType(doc.file_type, doc.original_filename);
          if (tip) metaParts.push(tip);
        }
        if (doc.file_size > 0) metaParts.push(formatFileSize(doc.file_size));
        if (uploadDateLabel) metaParts.push(`Încărcat la ${uploadDateLabel}`);
        if (ocr && typeof ocr.version === 'number' && ocr.version > 0) {
          metaParts.push(`OCR v${ocr.version}`);
        }

        // Overall pipeline tone for the collapsed-panel status pill.
        // Mirrors the helper inside `MemberDocumentCard` so the list card
        // and this panel header stay visually identical. Derives from the
        // same SSE-driven state the body pills consume, so the SSE flip
        // re-renders both pills in lockstep.
        const summaryPillTone: DocPipelineTone = (() => {
          if (
            pipelineStage === 'error' ||
            pipelineStage === 'failed' ||
            ocr?.summary_status === 'error' ||
            ocr?.classification_status === 'error'
          ) {
            return 'error';
          }
          if (isProcessing || isSummaryGenerating || isClassifying) return 'busy';
          if (
            pipelineStage === 'completed' ||
            pipelineStage === 'summary_completed' ||
            pipelineStage === 'classification_completed' ||
            ocrDone
          ) {
            return 'ready';
          }
          return 'idle';
        })();

        return (
          <details className="doc-view-details-panel" aria-label="Detalii document și procesare">
            <summary className="doc-view-details-summary">
              <div className="doc-view-details-summary-primary">
                <div className="doc-view-details-summary-text">
                  <h2 className="doc-view-details-title" title={doc.original_filename}>
                    {doc.original_filename}
                  </h2>
                  {metaParts.length > 0 && (
                    <small className="doc-details-meta">
                      {metaParts.map((part, i) => (
                        <span key={`meta-${i}`} className="doc-details-meta-item">
                          {part}
                        </span>
                      ))}
                    </small>
                  )}
                </div>
              </div>
              <DocPipelinePill
                tone={summaryPillTone}
                label={pipelineLabel}
                title={statusLabel[pipelineStage] || pipelineStage || undefined}
              />
              <svg
                className="doc-view-details-chevron"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </summary>

            <div className="doc-view-details-body">
              {/* Status pills — pipeline stage always; the AI pills are
                  power-user info, hidden in simplified mode. */}
              {ocr && (
                <div className="doc-view-details-section">
                  <div className="doc-view-status-row">
                    <div className="doc-view-status-item">
                      <div className="doc-view-field-label">
                        {isAdvanced ? 'Etapa pipeline' : 'Stare'}
                      </div>
                      <span className={pillClassForPipeline(pipelineStage)}>
                        <span className="doc-view-pill-dot" aria-hidden />
                        {pipelineLabel}
                      </span>
                    </div>
                    {isAdvanced && (
                      <>
                        <div className="doc-view-status-item">
                          <div className="doc-view-field-label">Rezumat AI</div>
                          <AiJobPill
                            status={ocr.summary_status}
                            errorDetail={ocr.summary_error}
                            detailLabel="Detalii eroare rezumat AI"
                          />
                        </div>
                        <div className="doc-view-status-item">
                          <div className="doc-view-field-label">Clasificare AI</div>
                          <AiJobPill
                            status={ocr.classification_status}
                            errorDetail={ocr.classification_error}
                            detailLabel="Detalii eroare clasificare AI"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Document metadata — date / classifier-mirrored types /
                  classifier facet pills. All optional, only the populated
                  rows render. The free-form `notes` block sits outside
                  this grid (rendered below) so it can span full width and
                  read like a paragraph rather than a label/value pair. */}
              {(() => {
                const docTypes = doc.document_types ?? [];
                const docTypesNorm = docTypes.map((t) => t.trim().toLowerCase()).filter(Boolean);
                const hasFacetRow = classifiers.some(
                  (c) => c.show_in_detail && (doc.facets?.[c.key] ?? []).filter(Boolean).length > 0,
                );
                const hasMetaGrid = !!doc.document_date || docTypes.length > 0 || hasFacetRow;
                if (!hasMetaGrid) return null;
                return (
                  <div className="doc-view-details-section">
                    <div className="doc-view-details-grid">
                      {doc.document_date && (
                        <div className="doc-view-detail-row">
                          <div className="doc-view-field-label">Data documentului</div>
                          <div className="doc-view-field-value">
                            {new Date(doc.document_date).toLocaleDateString('ro-RO')}
                          </div>
                        </div>
                      )}
                      {docTypes.length > 0 && (
                        <div className="doc-view-detail-row">
                          <div className="doc-view-field-label">
                            {isAdvanced ? 'Tip document' : 'Tip'}
                          </div>
                          <div className="doc-view-field-value doc-view-pill-row">
                            {docTypes.map((t, i) => (
                              <span key={`dtype-${i}-${t}`} className="badge badge-teal">
                                {formatClassifierValue(classifiers, 'document_type', t)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Classifier-derived facet sections. Driven entirely by the
                          admin-managed classifier registry: every classifier with
                          `show_in_detail` and at least one value on this document
                          renders its own labelled chip row. Order follows
                          `display_order` from the config so admins control how
                          the detail panel reads. Value labels come from the
                          registry's curated entries via `formatClassifierValue`. */}
                      {classifiers
                        .filter((c) => c.show_in_detail)
                        .map((c) => {
                          const values = (doc.facets?.[c.key] ?? []).filter(Boolean);
                          if (values.length === 0) return null;
                          // The `document_type` classifier mirrors `document_types`
                          // on the document; suppress it in simplified view, and
                          // also when the EAV facet values match the column 1:1.
                          if (c.key === 'document_type') {
                            if (!isAdvanced) return null;
                            const facetNorm = values
                              .map((v) => v.trim().toLowerCase())
                              .filter(Boolean);
                            if (
                              facetNorm.length === docTypesNorm.length &&
                              facetNorm.every((v) => docTypesNorm.includes(v))
                            ) {
                              return null;
                            }
                          }
                          const formatter = facetFormatterFor(classifiers, c.key);
                          const variant = facetBadgeVariantFor(c.key);
                          const label = isAdvanced
                            ? facetAdvancedLabelFor(c.key, c.label_ro)
                            : c.label_ro;
                          return (
                            <div key={`facet-${c.key}`} className="doc-view-detail-row">
                              <div className="doc-view-field-label">{label}</div>
                              <div className="doc-view-field-value doc-view-pill-row">
                                {values.map((v, i) => (
                                  <span key={`${c.key}-${i}-${v}`} className={`badge ${variant}`}>
                                    {formatter(v)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const trimmedNotes = (doc.notes ?? '').trim();
                if (!trimmedNotes) return null;
                return (
                  <div className="doc-view-details-section doc-view-details-section--notes">
                    <div className="doc-view-field-label">Note</div>
                    <p className="doc-view-notes-text">{trimmedNotes}</p>
                  </div>
                );
              })()}

              {/* Power-user technical block — raw OCR enum, confidence,
                  processed-at timestamp, page/table layout counts.
                  Hidden in simplified mode (the meta-line carries the
                  OCR version, which is the only piece a non-tech user
                  cares about). */}
              {ocr && (isAdvanced || (ocr.page_count != null && ocr.page_count > 0)) && (
                <div className="doc-view-details-section doc-view-details-section--tech">
                  <div className="doc-view-tech-grid">
                    {isAdvanced && (
                      <>
                        <div className="doc-view-tech-row">
                          <span className="doc-view-tech-label">Status OCR (tehnic)</span>
                          <span className="doc-view-tech-value doc-view-tech-value--mono">
                            {ocr.processing_status}
                          </span>
                        </div>
                        <div className="doc-view-tech-row">
                          <span className="doc-view-tech-label">Scor încredere OCR</span>
                          <span className="doc-view-tech-value">
                            {(ocr.confidence_score ?? 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="doc-view-tech-row">
                          <span className="doc-view-tech-label">Procesat la</span>
                          <span className="doc-view-tech-value">
                            {new Date(ocr.processing_date).toLocaleString('ro-RO')}
                          </span>
                        </div>
                      </>
                    )}
                    {ocr.page_count != null && ocr.page_count > 0 && (
                      <div className="doc-view-tech-row">
                        <span className="doc-view-tech-label">Pagini detectate</span>
                        <span className="doc-view-tech-value">{ocr.page_count}</span>
                      </div>
                    )}
                    {isAdvanced && ocr.table_count != null && ocr.table_count > 0 && (
                      <div className="doc-view-tech-row">
                        <span className="doc-view-tech-label">Tabele (layout)</span>
                        <span className="doc-view-tech-value">{ocr.table_count}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </details>
        );
      })()}

      {/* Tabs — shared `.tabs-bar` pill variant (same chrome as the
          Dashboard `dash-side-tabs` switcher). Visual rules live in
          `frontend/src/styles/tabs.css`; only the layout-spacing override
          for this page sits in `DocumentView.css` under `.doc-view-tabs`. */}
      <div
        className="tabs-bar tabs-bar--pills doc-view-tabs"
        role="tablist"
        aria-label="Secțiuni document"
      >
        {tabs.map((t) => {
          const selected = activeTab === t.id;
          return (
            <button
              key={t.id}
              ref={(el) => {
                tabsRef.current[t.id] = el;
              }}
              type="button"
              role="tab"
              id={`doc-view-tab-${t.id}`}
              aria-controls={`doc-view-tabpanel-${t.id}`}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              className={`tabs-bar__tab${selected ? ' is-active' : ''}`}
              onClick={() => setActiveTab(t.id)}
              onKeyDown={onTabsKeyDown}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="card">
        {activeTab === 'original' && (
          <section
            role="tabpanel"
            id="doc-view-tabpanel-original"
            aria-labelledby="doc-view-tab-original"
            tabIndex={0}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
          >
            {/* Page selector — only for grouped documents (file_count > 1).
                Each chip flips `previewFileIdx`; the auto-decrypt effect
                handles silent decrypt + the cache; cache hits swap
                instantly. */}
            {(doc.file_count ?? 1) > 1 && (doc.files?.length ?? 0) > 1 && (
              <div className="doc-view-page-selector" role="tablist" aria-label="Pagini document">
                {[...(doc.files || [])]
                  .sort((a, b) => a.file_index - b.file_index)
                  .map((f, idx) => {
                    const cached = decryptedByFileId.current.has(f.id);
                    const active = idx === previewFileIdx;
                    return (
                      <button
                        key={f.id}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        className={`doc-view-page-chip${active ? ' is-active' : ''}${cached ? ' is-cached' : ''}`}
                        onClick={() => {
                          if (active) return;
                          // Reset so the unlock CTA / spinner doesn't
                          // appear "stuck" from the previous page if
                          // the user retries before auto-decrypt
                          // settles.
                          setAutoDecryptStatus('idle');
                          // Synchronously hydrate the visible preview
                          // state in the SAME render as the index
                          // flip so the `<DocumentPreview>` below
                          // (which is keyed off `previewFileIdx`)
                          // remounts directly with the new page's
                          // url/blob/name/mime — never briefly with
                          // the previous page's data. For an
                          // already-decrypted page (cache hit) the
                          // remount is instant; for a cold page the
                          // empty state is shown until the auto-
                          // decrypt effect finishes the round-trip.
                          const hit = decryptedByFileId.current.get(f.id);
                          if (hit) {
                            setDecryptedUrl(hit.url);
                            setDecryptedBlob(hit.blob);
                            setDecryptedMime(hit.mime);
                            setDecryptedName(hit.name);
                          } else {
                            setDecryptedUrl(null);
                            setDecryptedBlob(null);
                            setDecryptedMime(null);
                            setDecryptedName(null);
                          }
                          setPreviewFileIdx(idx);
                        }}
                        title={f.original_filename || `Pagina ${idx + 1}`}
                      >
                        <span className="doc-view-page-chip-idx">{idx + 1}</span>
                        <span className="doc-view-page-chip-label">
                          {f.original_filename ? f.original_filename : `Pagina ${idx + 1}`}
                        </span>
                      </button>
                    );
                  })}
              </div>
            )}
            {/* Always-on E2EE: every doc renders through the decrypt
                path. The legacy `storage_mode === 'server'` branch is
                dead post-migration 0011; we keep the JSX simple by
                always taking the E2EE path. */}
            <div style={{ width: '100%' }}>
                {!decryptedUrl ? (
                  <div className="empty-state" style={{ padding: 'var(--sp-8)' }}>
                    <div className="empty-state-title">
                      {isAdvanced ? 'Document criptat end-to-end' : 'Document protejat'}
                    </div>
                    <div className="empty-state-text" style={{ maxWidth: 520, margin: '0 auto' }}>
                      {decrypting
                        ? 'Se deschide documentul…'
                        : autoDecryptStatus === 'trying'
                          ? 'Se încearcă deschiderea automată…'
                          : isAdvanced
                            ? 'Pentru a-l deschide, trebuie să deblochezi cheia de criptare (passphrase / passkey).'
                            : 'Pentru a-l deschide trebuie să confirmi codul tău de acces.'}
                    </div>
                    {!decrypting && autoDecryptStatus !== 'trying' && (
                      <div style={{ marginTop: 'var(--sp-4)', display: 'flex', justifyContent: 'center' }}>
                        <button
                          className="btn btn-primary"
                          onClick={async () => {
                            try {
                              setError('');
                              setDecrypting(true);
                              // Decrypt the currently selected page,
                              // not just the primary file — matters for
                              // grouped documents where the user may
                              // have already paged past page 1.
                              await decryptByIdx(previewFileIdx);
                            } catch (e: any) {
                              setError(
                                e?.message ||
                                  'Nu am putut deschide documentul criptat. Verifică passphrase-ul / passkey-ul și încearcă din nou.'
                              );
                            } finally {
                              setDecrypting(false);
                            }
                          }}
                        >
                          {isAdvanced ? 'Deblochează & decriptează' : 'Deschide documentul'}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <DocumentPreview
                    // Key the preview off the currently selected
                    // file (or off the document id for single-file
                    // docs, where it stays stable across renders).
                    // Switching pages on a grouped document changes
                    // the key, which forces React to fully unmount
                    // the previous viewer (PDF / DOCX / XLSX) and
                    // mount a fresh one for the new file — a robust
                    // safety net that eliminates an entire class of
                    // in-place state-cleanup bugs (stale pdf.js
                    // render tasks, leftover docx-preview DOM, cached
                    // intrinsic dims from the previous file, …) that
                    // would otherwise need to be tracked individually
                    // inside each sub-viewer's `useEffect` cleanups.
                    key={fileAtIdx(previewFileIdx)?.id ?? doc.id}
                    url={decryptedUrl}
                    blob={decryptedBlob}
                    filename={decryptedName || doc.original_filename}
                    mime={decryptedMime}
                  />
                )}
              </div>
          </section>
        )}

        {activeTab === 'markdown' && (
          <section
            role="tabpanel"
            id="doc-view-tabpanel-markdown"
            aria-labelledby="doc-view-tab-markdown"
            tabIndex={0}
          >
            <div className="flex items-center justify-between gap-2 w-full" style={{ marginBottom: 'var(--sp-3)' }}>
              {/* The Sursa/Previzualizare toggle dumps the raw markdown source — useful
                  for verifying OCR output verbatim, but visually intimidating. Hidden
                  in simplified mode (the rendered preview alone is shown). The
                  "Re-run OCR" action is reachable from the hero icon toolbar. */}
              {isAdvanced && ocr?.markdown_text && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowSource(!showSource)}
                style={{ fontSize: 'var(--text-xs)', padding: '4px 10px' }}
              >
                {showSource ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    Previzualizare
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                    Sursa
                  </>
                )}
              </button>
              )}
            </div>
            {ocr?.markdown_text ? (
              <>
                {showSource ? (
                  <div className="raw-text">{ocr.markdown_text}</div>
                ) : (
                  <ReactMarkdown
                    className="md-content"
                    remarkPlugins={[remarkGfm]}
                    urlTransform={(url, key, node) => {
                      // Allow data:image/* for inline images while keeping default URL sanitization.
                      if (key === 'src' && typeof url === 'string' && url.startsWith('data:image/')) return url;
                      return defaultUrlTransform(url);
                    }}
                  >
                    {unwrapFenced(ocr.markdown_text)}
                  </ReactMarkdown>
                )}
              </>
            ) : (
              <div className="empty-state" style={{ padding: 'var(--sp-8)' }}>
                <div className="empty-state-title">Markdown indisponibil</div>
                <div className="empty-state-text">Procesarea OCR poate fi inca in curs.</div>
              </div>
            )}
          </section>
        )}

        {activeTab === 'summary' && (
          <section
            role="tabpanel"
            id="doc-view-tabpanel-summary"
            aria-labelledby="doc-view-tab-summary"
            tabIndex={0}
          >
            {/* "Regenereaza rezumat" lives in the hero icon toolbar (3rd button). */}
            {isSummaryGenerating ? (
              <div style={{ padding: 'var(--sp-8)', textAlign: 'center' }}>
                <div className="status-spinner" style={{ margin: '0 auto var(--sp-4)', width: 24, height: 24 }} />
                <div style={{ color: 'var(--color-slate-500)', fontSize: 'var(--text-sm)' }}>Se genereaza rezumatul AI...</div>
              </div>
            ) : ocr?.summary ? (
              <ReactMarkdown
                className="md-content"
                remarkPlugins={[remarkGfm]}
                urlTransform={(url, key) => {
                  if (key === 'src' && typeof url === 'string' && url.startsWith('data:image/')) return url;
                  return defaultUrlTransform(url);
                }}
              >
                {ocr.summary}
              </ReactMarkdown>
            ) : ocr?.summary_status === 'error' ? (
              <div className="alert alert-error" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--sp-2)' }}>
                <strong>
                  {isAdvanced ? 'Eroare la generarea rezumatului.' : 'Nu am putut genera sumarul.'}
                </strong>
                {ocr.summary_error?.trim() ? (
                  <HelpTipButton
                    content={ocr.summary_error.trim()}
                    ariaLabel="Detalii eroare rezumat"
                    wide
                    buttonClassName="btn btn-secondary btn-sm"
                  >
                    Vezi detalii
                  </HelpTipButton>
                ) : (
                  <span style={{ fontSize: 'var(--text-sm)' }}>Eroare necunoscută.</span>
                )}
              </div>
            ) : (
              <div className="empty-state" style={{ padding: 'var(--sp-8)' }}>
                <div className="empty-state-title">Rezumat indisponibil</div>
                <div className="empty-state-text">
                  {ocr?.processing_status === 'completed'
                    ? 'Rezumatul nu a putut fi generat automat. Apasa butonul "Regenereaza rezumat" pentru a incerca din nou.'
                    : 'Procesarea OCR poate fi inca in curs. Rezumatul va fi generat automat dupa finalizare.'}
                </div>
              </div>
            )}
          </section>
        )}

        {activeTab === 'classification' && (
          <section
            role="tabpanel"
            id="doc-view-tabpanel-classification"
            aria-labelledby="doc-view-tab-classification"
            tabIndex={0}
          >
            {/* "Reclasifică / Clasifica document" lives in the hero icon toolbar (4th button). */}

            {isClassifying ? (
              <div style={{ padding: 'var(--sp-8)', textAlign: 'center' }}>
                <div className="status-spinner" style={{ margin: '0 auto var(--sp-4)', width: 24, height: 24 }} />
                <div style={{ color: 'var(--color-slate-500)', fontSize: 'var(--text-sm)' }}>Se clasifica documentul medical...</div>
              </div>
            ) : ocr?.classification ? (
              <ClassificationDisplay classification={ocr.classification} />
            ) : ocr?.classification_status === 'error' ? (
              <div className="alert alert-error" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--sp-2)' }}>
                <strong>Eroare la clasificare.</strong>
                {ocr.classification_error?.trim() ? (
                  <HelpTipButton
                    content={ocr.classification_error.trim()}
                    ariaLabel="Detalii eroare clasificare"
                    wide
                    buttonClassName="btn btn-secondary btn-sm"
                  >
                    Vezi detalii
                  </HelpTipButton>
                ) : (
                  <span style={{ fontSize: 'var(--text-sm)' }}>Eroare necunoscută.</span>
                )}
              </div>
            ) : (
              <div className="empty-state" style={{ padding: 'var(--sp-8)' }}>
                <div className="empty-state-title">Clasificare indisponibila</div>
                <div className="empty-state-text">
                  {ocr?.processing_status === 'completed'
                    ? 'Clasificarea nu a fost generata. Apasa butonul "Clasifica document" pentru a incepe.'
                    : 'Procesarea OCR poate fi inca in curs. Clasificarea va fi generata automat dupa finalizare.'}
                </div>
              </div>
            )}
          </section>
        )}
      </div>

      {(doc.file_count ?? 1) > 1 && doc.files && doc.files.length > 0 && (
        <section className="doc-view-files-panel" aria-labelledby="doc-view-files-heading">
          <h2 id="doc-view-files-heading" className="doc-view-details-title">
            Fișiere ({doc.file_count})
          </h2>
          <p className="doc-view-files-hint">
            Acest document grupat conține {doc.file_count} fișier
            {doc.file_count === 1 ? '' : 'e'}. Folosește butonul „Descarcă” din partea de
            sus pentru a obține arhiva ZIP completă, sau descarcă fișierele individuale de
            mai jos.
          </p>
          <ul className="doc-view-files-list">
            {[...doc.files]
              .sort((a, b) => a.file_index - b.file_index)
              .map((f) => (
                <DocumentFileRow
                  key={f.id}
                  documentId={doc.id}
                  fileId={f.id}
                  filename={f.original_filename}
                  mime={f.mime_type}
                  size={f.size_bytes}
                />
              ))}
          </ul>
        </section>
      )}

      <DocumentMetadataModal
        open={showEditDocMeta}
        onClose={() => setShowEditDocMeta(false)}
        document={doc}
        onSaved={loadDocument}
      />
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Șterge document?"
        variant="danger"
        confirmLabel="Șterge document"
        cancelLabel="Anulează"
        busy={deletingDocument}
        onConfirm={confirmDeleteDocument}
        onCancel={() => {
          if (!deletingDocument) setShowDeleteConfirm(false);
        }}
        message={
          <>
            <p style={{ margin: 0 }}>
              Ștergi definitiv documentul <strong>{doc.original_filename}</strong>?
            </p>
            <p style={{ margin: 0, color: 'var(--color-slate-600)' }}>
              Toate fișierele, OCR-ul și clasificarea vor fi șterse permanent.
              Acțiunea nu poate fi anulată.
            </p>
          </>
        }
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Inline RO fallback labels for the five enum-like fields the LLM emits
// directly on the classification object (`structure_type`, `care_context`,
// `source_type`, `risk_level`, `analyte_status`).
//
// These are *not* admin-managed classifiers — they're free-form enums that
// the classification prompt asks the LLM to fill in. We don't want them
// rendered as raw `critical_high` snake-case in the UI, so we ship a
// canonical RO dict here as a documented fallback.
//
// TODO(value-labels-config): once W3 ships the `classification.value_labels`
// system_config row, replace this with a runtime read from that row;
// admins will then override these without a frontend redeploy.
// ---------------------------------------------------------------------------
const VALUE_LABELS: Record<string, Record<string, string>> = {
  structure_type: {
    structured: 'Structurat',
    semi_structured: 'Semi-structurat',
    unstructured: 'Nestructurat',
    unknown: 'Necunoscut',
  },
  care_context: {
    inpatient: 'Internat',
    outpatient: 'Ambulator',
    emergency: 'Urgență',
    home_care: 'Îngrijire la domiciliu',
    unknown: 'Necunoscut',
  },
  source_type: {
    hospital: 'Spital',
    private_lab: 'Laborator privat',
    clinic: 'Clinică',
    other: 'Altă sursă',
    unknown: 'Necunoscut',
  },
  risk_level: {
    low: 'Scăzut',
    moderate: 'Moderat',
    high: 'Ridicat',
    unknown: 'Necunoscut',
  },
  analyte_status: {
    low: 'Scăzut',
    normal: 'Normal',
    high: 'Crescut',
    critical_high: 'Critic↑',
    critical_low: 'Critic↓',
    unknown: '?',
  },
};

function labelForEnumValue(group: keyof typeof VALUE_LABELS, value: string | null | undefined): string {
  if (!value) return '';
  return VALUE_LABELS[group]?.[value] ?? humanizeCode(value);
}

/**
 * Walk a JSON path expression (as authored on the Classifier registry) against
 * a raw classification object and yield a flat string[] of values.
 *
 * Supported shapes:
 *   - `a.b.c`     → nested dict access, terminal string or string[]
 *   - `a[].b`     → array of objects, harvest `.b` from each element
 *   - `a.b[]`     → array of strings, return them all
 *
 * Anything that doesn't resolve to a string / string-array is filtered out so
 * the chip rows never render `[object Object]`. Returns an empty array when
 * the path is empty or the value tree is missing the requested key.
 */
function valuesAtJsonPath(root: unknown, path: string | undefined | null): string[] {
  if (!path || typeof root !== 'object' || root == null) return [];
  let cursors: unknown[] = [root];
  for (const segment of path.split('.')) {
    if (!segment) continue;
    const wantsArray = segment.endsWith('[]');
    const key = wantsArray ? segment.slice(0, -2) : segment;
    const next: unknown[] = [];
    for (const cur of cursors) {
      if (cur == null || typeof cur !== 'object') continue;
      const val = (cur as Record<string, unknown>)[key];
      if (val == null) continue;
      if (wantsArray) {
        if (Array.isArray(val)) next.push(...val);
      } else {
        next.push(val);
      }
    }
    cursors = next;
  }
  const out: string[] = [];
  for (const cur of cursors) {
    if (typeof cur === 'string') {
      const s = cur.trim();
      if (s) out.push(s);
    } else if (Array.isArray(cur)) {
      for (const x of cur) {
        if (typeof x === 'string') {
          const s = x.trim();
          if (s) out.push(s);
        }
      }
    }
  }
  return out;
}

/**
 * Render a single raw LLM classification payload.
 *
 * The component is domain-agnostic: it walks the active classifier
 * registry and renders one chip row per classifier (`show_in_detail`),
 * resolving codes → display labels through `formatClassifierValue`. The
 * medical-shaped extras (analytes, panels, clinical flags, risk, tag
 * groups, imaging facets) are grouped under "Detalii structurate" and
 * only render when the raw classification payload still ships them.
 */
const ClassificationDisplay: React.FC<{ classification: DocumentClassification }> = ({ classification }) => {
  const { classifiers } = useClassifiersConfig();
  const doc = (classification.document ?? {}) as Record<string, unknown>;

  const confRaw = typeof doc.confidence === 'number' ? doc.confidence : 0;
  const confidencePct = Math.round(confRaw * 100);
  const confidenceColor = confidencePct >= 80 ? 'var(--color-green-600)' : confidencePct >= 50 ? 'var(--color-amber-600)' : 'var(--color-red-600)';
  const lang = typeof doc.language === 'string' ? doc.language : 'ro';
  const docNotes = typeof doc.notes === 'string' ? doc.notes : null;
  const careContext = typeof doc.care_context === 'string' ? doc.care_context : null;
  const sourceType = typeof doc.source_type === 'string' ? doc.source_type : null;
  const structureType = typeof doc.structure_type === 'string' ? doc.structure_type : null;

  // Filter out "none" / "unknown" sentinels the LLM occasionally emits on
  // fields that don't apply (e.g. imaging_modality on a lab report) so we
  // don't render empty rows.
  const isSentinel = (s: string) => {
    const v = s.trim().toLowerCase();
    return !v || v === 'none' || v === 'unknown' || v === 'n/a';
  };

  // Resolve each `show_in_detail` classifier's values by walking its
  // `json_path` against the raw classification payload. Empty path or no
  // matches → the row is skipped (no `—` placeholder noise).
  const facetSections = classifiers
    .filter((c) => c.show_in_detail)
    .map((c) => {
      const values = valuesAtJsonPath(classification, c.json_path).filter((v) => !isSentinel(v));
      return { classifier: c, values };
    })
    .filter((s) => s.values.length > 0);

  // Only data the data-driven `facetSections` grid above can't handle
  // lives here: structured Risc + the Analiti table (no equivalent
  // classifier shape). Everything else (clinical_flag, clinical_tag,
  // technical_tag, biomarker_tag, panel, …) now renders via the
  // dynamic chip grid driven by `c.json_path` against the registry,
  // so the legacy hardcoded clones below have been removed.
  const risk = classification.risk;
  const analytes = classification.analytes ?? [];

  const hasStructuredExtras = Boolean(
    (risk && (risk.global_risk_level || risk.severity_score != null)) ||
      analytes.length > 0
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
      <div className="info-grid">
        <div className="info-item">
          <div className="info-label">Limba document</div>
          <div className="info-value">{lang === 'ro' ? 'Română' : lang === 'en' ? 'Engleză' : lang}</div>
        </div>
        <div className="info-item">
          <div className="info-label">Încredere clasificare</div>
          <div className="info-value" style={{ color: confidenceColor, fontWeight: 600 }}>{confidencePct}%</div>
        </div>
      </div>

      {facetSections.length > 0 && (
        <div className="info-grid">
          {facetSections.map(({ classifier: c, values }) => (
            <div className="info-item" key={`cls-${c.key}`} style={{ maxWidth: '100%' }}>
              <div className="info-label">{c.label_ro}</div>
              <div className="info-value" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                {values.map((v, i) => (
                  <span
                    key={`${c.key}-${i}-${v}`}
                    className={`badge badge-dot ${facetBadgeVariantFor(c.key)}`}
                  >
                    {formatClassifierValue(classifiers, c.key, v)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {(careContext || sourceType || structureType) && (
        <div>
          <div className="info-label" style={{ marginBottom: 'var(--sp-2)' }}>Context</div>
          <div className="info-grid">
            {careContext && (
              <div className="info-item">
                <div className="info-label">Îngrijire</div>
                <div className="info-value" style={{ fontSize: 'var(--text-sm)' }}>
                  {labelForEnumValue('care_context', careContext)}
                </div>
              </div>
            )}
            {sourceType && (
              <div className="info-item">
                <div className="info-label">Sursă</div>
                <div className="info-value" style={{ fontSize: 'var(--text-sm)' }}>
                  {labelForEnumValue('source_type', sourceType)}
                </div>
              </div>
            )}
            {structureType && (
              <div className="info-item">
                <div className="info-label">Structură</div>
                <div className="info-value" style={{ fontSize: 'var(--text-sm)' }}>
                  {labelForEnumValue('structure_type', structureType)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {hasStructuredExtras && (
        <div>
          <div className="info-label" style={{ marginBottom: 'var(--sp-2)' }}>Detalii structurate</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
            {risk && (risk.global_risk_level || risk.severity_score != null) && (
              <div>
                <div className="info-label" style={{ marginBottom: 'var(--sp-2)' }}>Risc</div>
                <div className="info-grid">
                  {risk.global_risk_level && (
                    <div className="info-item">
                      <div className="info-label">Nivel</div>
                      <div className="info-value">
                        <span className="badge badge-amber">
                          {labelForEnumValue('risk_level', risk.global_risk_level)}
                        </span>
                      </div>
                    </div>
                  )}
                  {risk.severity_score != null && (
                    <div className="info-item">
                      <div className="info-label">Severitate (0–1)</div>
                      <div className="info-value" style={{ fontSize: 'var(--text-sm)' }}>
                        {typeof risk.severity_score === 'number' ? risk.severity_score.toFixed(3) : risk.severity_score}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {analytes.length > 0 && (
              <div>
                <div className="info-label" style={{ marginBottom: 'var(--sp-2)' }}>Analiți</div>
                <div style={{ overflowX: 'auto', maxHeight: 280, border: '1px solid var(--color-slate-200)', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-xs)' }}>
                    <thead>
                      <tr style={{ background: 'var(--color-slate-50)', textAlign: 'left' }}>
                        <th style={{ padding: 'var(--sp-2)' }}>Nume</th>
                        <th style={{ padding: 'var(--sp-2)' }}>Valoare</th>
                        <th style={{ padding: 'var(--sp-2)' }}>Ref.</th>
                        <th style={{ padding: 'var(--sp-2)' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytes.slice(0, 60).map((a, i) => (
                        <tr key={i} style={{ borderTop: '1px solid var(--color-slate-100)' }}>
                          <td style={{ padding: 'var(--sp-2)' }}>{a.name ?? '—'}</td>
                          <td style={{ padding: 'var(--sp-2)' }}>
                            {a.value ?? '—'} {a.unit ? String(a.unit) : ''}
                          </td>
                          <td style={{ padding: 'var(--sp-2)' }}>{a.reference_range ?? '—'}</td>
                          <td style={{ padding: 'var(--sp-2)' }}>
                            {a.status ? labelForEnumValue('analyte_status', a.status) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {docNotes && (
        <div>
          <div className="info-label" style={{ marginBottom: 'var(--sp-2)' }}>Note (din document)</div>
          <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--color-slate-700)', lineHeight: 1.6 }}>{docNotes}</p>
        </div>
      )}
    </div>
  );
};

export default DocumentView;
